import { LoadingButton } from '@mui/lab';
import { styled, Typography } from '@mui/material';
import { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAuth } from '../../components/Providers/AuthProvider';
import { useFetchData } from '../../hooks/useFetchData';
import { useIndexRun } from '../../hooks/useIndexRun';
import { getFolder } from '../../redux/actions/folderActions';
import { getIndexedFiles } from '../../redux/actions/indexActions';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { clearIndexFiles, selectIndexes } from '../../redux/reducers/indexReducer';
import { AppDispatch } from '../../redux/store';
import {
  checkProjectFolderExists,
  deleteProjectIndex,
  rebuildProjectIndex,
} from '../../services/dealGPT';
import { ProjectWithLastScanned } from '../../Types/dealGPT';
import { FileView, IndexType } from '../../Types/enums';
import { projectPathUrl } from '../../util/routesUrls';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import FileTable from '../Files/FileTable';
import { useNotifications } from '../Providers/NotificationsProvider';

const HeaderLabel = styled(Typography)({
  fontWeight: '700',
});

const ButtonContainer = styled('div')({
  display: 'flex',
  gap: '16px',
});

const StyledLoadingButton = styled(LoadingButton)({
  padding: '8px 22px',
  width: '224px',
  height: '36px',
  boxShadow:
    '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
});

const IndexSection = styled('div')({
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px',
  backgroundColor: '#fafafa',
});

export type FileIndexProps = {
  indexToUse: IndexType;
  indexId: string;
  onGoToGraph?: (graphDocId: string) => void;
};

const FileIndex = ({ indexToUse, indexId, onGoToGraph }: FileIndexProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const { indexFiles, totalFiles, loading } = useSelector(selectIndexes);
  const { currentFolder } = useSelector(selectFolders);
  const { isDealGPTAdmin, fetchTokens } = useAuth();
  const { refetchNotifications } = useNotifications();
  const [fetchFiles, setFetchFiles] = useState<boolean>(true);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOpenRAG, setDeleteOpenRAG] = useState(false);
  const [deleteOpenGraphRAG, setDeleteOpenGraphRAG] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(false);
  const isHandlingPollRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data: project, refetch: refetchProject } = useFetchData<ProjectWithLastScanned>({
    urlPath: projectPathUrl(currentFolder?.projectId ?? '', isDealGPTAdmin),
    skip: !currentFolder?.projectId,
  });

  const effectiveIndexId = useMemo(() => {
    if (isDeleting) return undefined;
    return indexId || currentFolder?.indexIds?.[0] || undefined;
  }, [isDeleting, indexId, currentFolder?.indexIds]);

  const { latestIndexRunValues, refetchLatestIndexRunValues } = useIndexRun(effectiveIndexId);

  useEffect(() => {
    if (
      [IndexType.GRAPH_RAG, IndexType.OPENAI].includes(indexToUse) &&
      effectiveIndexId &&
      fetchFiles
    ) {
      const sortField = sortModel[0]?.field;
      const sortDirection = sortModel[0]?.sort;

      dispatch(
        getIndexedFiles({
          indexId: effectiveIndexId,
          indexType: indexToUse,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortField,
          sortDirection,
        })
      )
        .unwrap()
        .then(() => {
          setFetchFiles(false);
        });
    }
  }, [
    fetchFiles,
    effectiveIndexId,
    indexToUse,
    dispatch,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
  ]);

  useEffect(() => {
    if (!shouldPoll || isHandlingPollRef.current) return;

    isHandlingPollRef.current = true;
    setShouldPoll(false);

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = undefined;
    }

    if (!currentFolder?.indexIds?.length && currentFolder?.id) {
      void dispatch(getFolder({ folderId: currentFolder.id })).finally(() => {
        isHandlingPollRef.current = false;
      });
    } else {
      isHandlingPollRef.current = false;
    }

    if (effectiveIndexId) {
      void refetchLatestIndexRunValues();
    }
    setFetchFiles(Boolean(effectiveIndexId));

    if (
      !isDeleting &&
      ((currentFolder?.id && !effectiveIndexId) || latestIndexRunValues?.status !== 'COMPLETE')
    ) {
      pollTimeoutRef.current = setTimeout(() => {
        setShouldPoll(true);
      }, 10000);
    }
  }, [
    dispatch,
    shouldPoll,
    latestIndexRunValues?.status,
    currentFolder?.id,
    currentFolder?.indexIds?.length,
    effectiveIndexId,
    isDeleting,
    refetchLatestIndexRunValues,
  ]);

  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const getRelativePath = (sharepointFolderURL: string): string => {
    const cleanedFolderURL = sharepointFolderURL.replace('/:f:/r/', '/').split('?')[0];
    const folderUrlObj = new URL(cleanedFolderURL);
    const fullFolderPath = decodeURIComponent(folderUrlObj.pathname);
    let marker = '/Shared Documents/';
    let markerIndex = fullFolderPath.indexOf(marker);
    if (markerIndex === -1) {
      // Channel folder structure is different
      marker = '@thread.tacv2/';
      markerIndex = fullFolderPath.indexOf(marker);
    }

    return markerIndex !== -1
      ? '/' + fullFolderPath.substring(markerIndex + marker.length)
      : fullFolderPath;
  };

  const handleUpdateIndex = async (indexType: IndexType) => {
    if (!currentFolder?.projectId || !project) return;

    await fetchTokens();

    const { channelId, teamsId, sharepointFolderURL } = project;
    try {
      const response = await checkProjectFolderExists(teamsId, channelId, sharepointFolderURL);
      if (!response) {
        enqueueSnackbar('Folder not found, check the root folder URL', { variant: 'error' });
        return;
      }
    } catch (error) {
      enqueueSnackbar('Failed to check folder existence', { variant: 'error' });
      return;
    }

    const projectIndexResult = await rebuildProjectIndex(currentFolder.projectId, indexType);
    if (!projectIndexResult) {
      enqueueSnackbar('Failed to start indexing process', { variant: 'error' });
      return;
    }
    refetchNotifications();
    setShouldPoll(true);
  };
  const handleDeleteIndex = async (indexType: IndexType) => {
    setIsDeleting(true);
    if (!currentFolder?.projectId || !currentFolder?.id) {
      enqueueSnackbar('Unable to delete index right now. Please refresh and try again.', {
        variant: 'error',
      });
      setIsDeleting(false);
      return;
    }

    try {
      await deleteProjectIndex(currentFolder.projectId, indexType);
      setShouldPoll(false);
      dispatch(clearIndexFiles());
      setFetchFiles(false);

      await Promise.all([
        refetchProject(),
        dispatch(getFolder({ folderId: currentFolder.id })).unwrap(),
      ]);
    } catch (error) {
      enqueueSnackbar('Failed to delete index', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
      setFetchFiles(true);
    },
    [setPaginationModel, setFetchFiles]
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);
      if (!effectiveIndexId) return;

      dispatch(
        getIndexedFiles({
          indexId: effectiveIndexId,
          indexType: indexToUse,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortField: sortModel[0]?.field,
          sortDirection: sortModel[0]?.sort,
          filterModel: model,
        })
      );
    },
    [
      dispatch,
      effectiveIndexId,
      indexToUse,
      paginationModel.page,
      paginationModel.pageSize,
      sortModel,
    ]
  );

  useEffect(() => {
    return () => {
      dispatch(clearIndexFiles());
    };
  }, [dispatch]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      setFetchFiles(true);
    },
    [setSortModel, setFetchFiles]
  );

  const lastIndexText = useMemo(() => {
    if (!latestIndexRunValues?.lastFileIndexRunTime) return null;

    const date = new Date(latestIndexRunValues.lastFileIndexRunTime).toLocaleString();
    const user = latestIndexRunValues.lastUserIndexRun;
    return `Last Indexed at: ${date}${user ? ` by ${user}` : ''}`;
  }, [latestIndexRunValues]);

  const FolderPathText = useMemo(() => {
    if (!project?.sharepointFolderURL) return null;

    const folder = getRelativePath(project.sharepointFolderURL);

    return `Folder Path: ${folder}`;
  }, [project?.sharepointFolderURL]);

  const indexConfig = useMemo(() => {
    switch (indexToUse) {
      case IndexType.OPENAI:
        return {
          type: IndexType.OPENAI,
          label: 'RAG',
          title: 'RAG Indexing',
          syncText: 'Sync RAG Index',
          deleteText: 'Delete RAG Index',
          indexId: effectiveIndexId,
          deleteOpen: deleteOpenRAG,
          setDeleteOpen: setDeleteOpenRAG,
        };

      case IndexType.GRAPH_RAG:
        return {
          type: IndexType.GRAPH_RAG,
          label: 'Graph RAG',
          title: 'Graph RAG Indexing',
          syncText: 'Sync Graph RAG Index',
          deleteText: 'Delete Graph RAG Index',
          indexId: effectiveIndexId,
          deleteOpen: deleteOpenGraphRAG,
          setDeleteOpen: setDeleteOpenGraphRAG,
        };

      default:
        return null;
    }
  }, [indexToUse, effectiveIndexId, deleteOpenRAG, deleteOpenGraphRAG]);

  return (
    <div style={{ paddingBottom: '100px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <HeaderLabel>Project Files</HeaderLabel>
          {FolderPathText && <Typography variant="body2">{FolderPathText}</Typography>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', width: 'min-content' }}>
          <ButtonContainer>
            <StyledLoadingButton
              variant="contained"
              loadingPosition="end"
              endIcon={<></>}
              disabled={isDeleting}
              onClick={async () => {
                await refetchProject();
                if (effectiveIndexId) {
                  refetchLatestIndexRunValues();
                }
                setFetchFiles(Boolean(effectiveIndexId));
              }}
            >
              Refresh Page
            </StyledLoadingButton>
          </ButtonContainer>
          <Typography variant="caption" color="text.secondary" sx={{ marginTop: 1 }}>
            Indexing time varies by data room size: smaller data rooms (&lt;50 files) typically
            index in minutes, while larger ones (&gt;1GB) may take up to two hours.
          </Typography>
        </div>
      </div>

      {indexConfig && (
        <IndexSection>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <HeaderLabel variant="h6">{indexConfig.title}</HeaderLabel>

              <Typography variant="body2">
                Status:{' '}
                {latestIndexRunValues?.status ||
                  'No status available, please sync and update files'}
              </Typography>

              {lastIndexText && <Typography variant="body2">{lastIndexText}</Typography>}
            </div>

            <ButtonContainer>
              <StyledLoadingButton
                variant="contained"
                loadingPosition="end"
                endIcon={<></>}
                disabled={isDeleting}
                onClick={() => handleUpdateIndex(indexConfig.type)}
              >
                {indexConfig.syncText}
              </StyledLoadingButton>

              {isDealGPTAdmin && indexConfig.indexId && (
                <>
                  <DeleteConfirmationDialog
                    isOpen={indexConfig.deleteOpen}
                    setIsOpen={indexConfig.setDeleteOpen}
                    onDelete={() => handleDeleteIndex(indexConfig.type)}
                  />

                  <StyledLoadingButton
                    variant="contained"
                    loadingPosition="end"
                    endIcon={<></>}
                    loading={isDeleting}
                    onClick={() => indexConfig.setDeleteOpen(true)}
                  >
                    {indexConfig.deleteText}
                  </StyledLoadingButton>
                </>
              )}
            </ButtonContainer>
          </div>
        </IndexSection>
      )}

      <FileTable
        files={indexFiles}
        fileView={FileView.PROJECT}
        setIsLoadingThis={() => {}}
        total={totalFiles}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        isLoading={loading}
        onGoToGraph={onGoToGraph}
      />
    </div>
  );
};

export default FileIndex;
